/**
 * Film z YouTube w sekcji hero: sam obraz (bez przycisków YouTube),
 * zapętlony i zawsze bez dźwięku; kliknięcie w film nic nie robi.
 *
 * Ramka z parametrami odtwarzania jest wpisana w index.html, więc film
 * startuje razem ze stroną. Tutaj tylko podpinamy się do niej przez
 * IFrame API, żeby wyłączyć napisy i wznawiać film, gdyby się zatrzymał.
 * Safari blokuje autostart ramek z innych domen (także wyciszonych), dopóki
 * odwiedzający czegoś nie kliknie. Wtedy zamiast ekranu YouTube pokazujemy
 * sam kadr z meczu, bez przycisków (klik przechodzi do odtwarzacza), a pierwsza
 * interakcja na stronie próbuje uruchomić film. Blokada kliknięć (pauzy)
 * włącza się dopiero, gdy film leci.
 */

/** Po tylu ms bez startu uznajemy, że przeglądarka wstrzymała autostart. */
export const AUTOPLAY_GRACE_MS = 1500;

/** Parametry, z którymi ramka w index.html ma osadzać film. */
export const REQUIRED_EMBED_PARAMS = {
  autoplay: "1",
  mute: "1",
  controls: "0",
  disablekb: "1",
  fs: "0",
  iv_load_policy: "3",
  cc_load_policy: "0",
  rel: "0",
  playsinline: "1",
  loop: "1",
  enablejsapi: "1",
};

/**
 * @param {number} state stan odtwarzacza YouTube
 * @param {{PLAYING: number, BUFFERING: number}} states stałe YT.PlayerState
 * @returns {boolean} czy film leci (wtedy blokujemy kliknięcia)
 */
export function isPlayingState(state, states) {
  return state === states.PLAYING || state === states.BUFFERING;
}

/**
 * @param {string} src adres ramki YouTube
 * @returns {string[]} nazwy parametrów, których brakuje albo mają złą wartość
 */
export function missingEmbedParams(src) {
  const url = new URL(src);
  const videoId = url.pathname.split("/").pop();
  const expected = { ...REQUIRED_EMBED_PARAMS, playlist: videoId };
  return Object.entries(expected)
    .filter(([key, value]) => url.searchParams.get(key) !== value)
    .map(([key]) => key);
}

function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  return new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === "function") previous();
      resolve(window.YT);
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    document.head.appendChild(script);
  });
}

export function initHeroVideo() {
  const iframe = document.getElementById("heroPlayer");
  if (!iframe) return;
  const frame = iframe.closest(".vid");

  loadYouTubeApi().then((YT) => {
    const player = new YT.Player(iframe, {
      events: {
        onReady: () => {
          player.mute();
          player.playVideo();
          if (typeof player.unloadModule === "function") player.unloadModule("captions");

          setTimeout(() => {
            if (!isPlayingState(player.getPlayerState(), YT.PlayerState)) {
              frame.classList.add("needs-start");
            }
          }, AUTOPLAY_GRACE_MS);

          const startOnInteraction = () => {
            player.mute();
            player.playVideo();
          };
          for (const type of ["pointerdown", "touchstart", "keydown"]) {
            document.addEventListener(type, startOnInteraction, { once: true, passive: true });
          }
        },
        onStateChange: (event) => {
          const playing = isPlayingState(event.data, YT.PlayerState);
          frame.classList.toggle("is-playing", playing);
          if (playing) frame.classList.remove("needs-start");
          // Zatrzymany lub zakończony film wznawiamy, żeby obraz zawsze leciał.
          if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
            player.playVideo();
          }
        },
      },
    });
  });
}
